<template>
  <c-input-container
    v-bind="$attrs"
    :class="classes"
    :validation="isOpened ? '' : validation"
    :display-validation="displayValidation"
  >
    <div class="inner" @click.once="touched = true">
      <div
        class="input"
        :tabindex="tabindex"
        @click="openingHandler"
        @focus="handlerFocus"
        v-click-outside="outside"
      >
        <span
          ref="searchable"
          contenteditable
          :class="['selected', { '-placeholder': selected === placeholder }]"
          @keydown.self.down.prevent="pointerForward"
          @keydown.self.up.prevent="pointerBackward"
          @keydown.enter.tab.stop.self="addPointerElement"
          @keydown.tab.stop.self="escHandler"
          @keyup.esc.passive="escHandler"
          @input="search"
          @click="resetQuery"
        >
          {{ typeof selected === 'object' && selected[displayBy] || selected }}
        </span>

        <c-icon size="10" class="icon" icon="chevron-down" />
      </div>

      <div v-if="isOpened && options.length" class="items">
        <div
          v-for="(option, index) in options"
          :key="index"
          :class="['item', { '-selected': isSelected(option), '-active': index === pointer }]"
          aria-hidden="true"
          @click.stop="selected = index"
          @mouseenter.self="pointerSet(index)"
        >
          <span class="text">{{ option[displayBy] }}</span>
        </div>
      </div>
    </div>
  </c-input-container>
</template>

<script>
import * as object from '@helpers/object'
import matches from '@helpers/matches'

export default {
  directives: {
    'click-outside': {
      bind: function(el, binding, vNode) {
        const handler = (e) => {
          if (!el.contains(e.target) && el !== e.target) {
          	binding.value(e)
          }
        }
        el.__vueClickOutside__ = handler

        document.addEventListener('click', handler)
      },

      unbind: function(el, binding) {
        document.removeEventListener('click', el.__vueClickOutside__)
        el.__vueClickOutside__ = null
      }
    }
  },

  props: {
    tabindex: {
      type: Number,
      default: 0
    },
    value: {
      type: [String, Object, Number],
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    displayBy: {
      type: String,
      default: 'name',
      required: true
    },
    trackBy: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Selecione'
    },
    closeOnSelect: Boolean,
    clearOnSelect: Boolean,
    validation: String,
    disabled: Boolean,
    success: Boolean,
    hasError: Boolean,
  },

  data() {
    return {
      pointer: -1,
      touched: false,
      isOpened: false,
      searchQuery: ''
    }
  },

  computed: {
    classes () {
      return [ 'c-select',
        {
          '-is-opened': this.isOpened,
          '-has-validation': this.validation || this.success,
          '-disabled': this.disabled,
          '-success': this.success
        }
      ]
    },
    selected: {
      get () {
        if (this.placeholder && this.value === '') return this.placeholder
        return this.value
          ? this.options.find(item => this.trackBy ? item[this.trackBy] === this.value : object.equals(item, this.value))
          : this.placeholder
      },
      set (index) {
        if (index >= 0) {
          const tracked = this.trackBy
            ? this.options[index][this.trackBy]
            : this.options[index]

          this.outside()
          this.$emit('input', tracked)
        }
      }
    },
    options () {
      return this.searchQuery
        ? this.items.filter((item) => {
          const itemName = this.displayBy ? item[this.displayBy] : item
          return typeof itemName === 'string'
            ? matches(this.searchQuery.toLowerCase(), itemName.toLowerCase())
            : matches(this.searchQuery.toString().toLowerCase(), itemName.toString().toLowerCase())
        })
        : this.items
    },
    displayValidation () {
      return !!this.validation && (this.touched || this.hasError)
    }
  },

  methods: {
    handlerFocus () {
      this.isOpened = true
      this.resetQuery()
      this.setFocus()
    },
    openingHandler () {
      if (this.isOpened) this.isOpened = false
      this.isOpened = true

      this.resetQuery()
      this.setFocus()
    },
    escHandler () {
      if (this.isOpened) {
        this.outside()
        this.unsetFocus()
      }
    },
    resetQuery () {
      this.$refs['searchable'].innerHTML = ''
      this.$emit('input', this.value)
    },
    isSelected (item) {
      if (!this.selected) return false

      return this.trackBy
        ? this.selected[this.trackBy] === item[this.trackBy]
        : object.equals(item, this.value)
    },
    outside () {
      this.isOpened = false
      this.blur()
    },
    clear () {
      this.clearOnSelect ? this.itemSelected = '' : this.itemSelected
    },
    search ({ target: { textContent } }) {
      this.isOpened = true
      this.searchQuery = textContent
    },
    blur () {
      setTimeout(() => {
        const contentDefault = this.items.find(item => item.name === this.value)
        const content = typeof this.selected === 'object' && this.selected[this.displayBy] || contentDefault && contentDefault[this.displayBy] || this.value || this.placeholder

        if (this.$refs['searchable']) this.$refs['searchable'].innerHTML = content
        this.searchQuery = ''
        this.pointerReset()
      }, 100)
    },
    pointerReset () {
      this.pointer = -1
    },
    pointerSet (option) {
      this.pointer = option
    },
    pointerForward () {
      if (this.pointer < this.items.length - 1) {
        this.pointer++
      }
    },
    pointerBackward () {
      if (this.pointer > 0) {
        this.pointer--
      }
    },
    setFocus () {
      this.$refs.searchable.focus()
    },
    unsetFocus () {
      this.$refs.searchable.blur()
    },
    addPointerElement ({ key } = 'Enter') {
      if (this.items.length > 0 && key === 'Enter') {
        this.selected = this.pointer

        this.$nextTick(() => {
          this.unsetFocus()
          this.outside()
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@style/reference';

$c-select-border-color: #E9EAEE;
$c-select-highlight-background: #f3f4f6 !default;

$c-input-disabled-background-color: #eaedef !default;
$c-input-disabled-color:            #bdc0d1 !default;

.c-select {
  position: relative;

  &.-disabled {
    cursor: default;
    pointer-events: none;

    & > .inner > .input {
      background-color: $c-input-disabled-background-color;
      border-color: $c-input-disabled-background-color;

      pointer-events: none;

      & > .selected,
      & > .selected.-placeholder {
        color: $c-input-disabled-color;
        font-weight: 300;
      }

      & > .icon { fill: $c-input-disabled-color; }
    }
  }

  & > .inner {
    position: relative;
    width: 100%;

    & > .input {
      position: relative;
      border: 1px solid $c-select-border-color;
      display: flex;
      align-items: center;
      height: 40px;
      background-color: white;
      border-radius: 3px;
      font-size: 14px;
      transition: border-color .3s ease, box-shadow .3s ease;
      cursor: pointer;

      & > .selected {
        display: inline-flex;
        align-items: center;

        color: $title-color;
        padding-left: 10px;
        padding-right: 35px;

        line-height: 40px;
        min-height: inherit;
        outline: none;

        &.-placeholder {
          color: $icon-color;
          font-size: 14px;
          font-weight: 400;
        }
      }
      & > .icon {
        position: absolute;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        transition: transform 500ms ease;
      }
      &:hover,
      &:active,
      &:focus {
        border-color: $secondary-color-placeholder;
        border-color: var(--color-secondary);
        box-shadow: 0 0 0 2px $secondary-color-placeholder;
        box-shadow: 0 0 0 2px var(--color-secondary);
      }
    }

    .items {
      border-radius: 8px;
      max-height: 205px;
      overflow-y: auto;
      overflow-x: hidden;
      z-index: 2;
      width: 100%;
      box-sizing: border-box;
      background-color: #fff;
      position: absolute;
      left: 0;
      top: calc(100% + 2px);

      border: 1px solid #E8E8E8;
      border-top: none;

      & > .item {
        cursor: pointer;
        min-height: 40px;
        display: flex;
        align-items: center;

        &.-selected { font-weight: 700; }
        &.-active { background-color: $c-select-highlight-background; }

        &:first-child {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }

        &:last-child {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }

        & > .text {
          font-size: 14px;
          color: $title-color;
          transition: color .3s ease;
          color: $text-color;
          padding: 10px;
          padding-left: 12px;
        }

        &:hover {
          background-color: $c-select-highlight-background;

          & > .text {
            color: $primary-color-placeholder;
            color: var(--color-primary);
          }
        }
      }
    }

    &.-is-opened { @extend %c-select-opened; }
  }
}

%c-select-opened {
  & > .input {
    border-radius: 3px 3px 0 0;

    & > .entry > .icon { transform: translateY(-50%) rotate(-180deg); }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CComponents/CSelect.vue